import { useEffect, useMemo, useRef, useState } from 'react';
import { CardRecord } from '../db/indexedDb';
import { VocabPracticeCard, normalizeVocabMeta } from '../services/srsQueue/vocabQueue';
import { evaluateVocabSpeaking, VocabSpeakingResult } from './speaking/vocabEvaluator';

type Props = {
  card: CardRecord;
};

type RecordingState = 'idle' | 'recording' | 'evaluating';

const levelLabels: Record<string, { label: string; tone: 'success' | 'info' | 'warn' | 'danger' }> = {
  perfect: { label: '完美 🎯', tone: 'success' },
  good: { label: '良好 ✅', tone: 'success' },
  acceptable: { label: '可接受 ⚠️', tone: 'info' },
  poor: { label: '需改进 ❌', tone: 'danger' },
};

/** 获取浏览器支持的音频 MIME 类型 */
const getSupportedMimeType = (): string => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/wav',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return ''; // 使用浏览器默认
};

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const SpeakingVocabCard = ({ card }: Props) => {
  const practice = card as VocabPracticeCard;
  const practiceId = practice.practiceId ?? card.id;

  const normalized = useMemo(() => normalizeVocabMeta(card), [card]);

  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [result, setResult] = useState<VocabSpeakingResult | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    // 重置状态
    setResult(null);
    setAudioUrl(null);
  }, [practiceId]);

  // 清理资源
  useEffect(() => {
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [audioUrl]);

  const startRecording = async () => {
    if (recordingState !== 'idle') return;
    if (!navigator.mediaDevices?.getUserMedia) {
      alert('当前浏览器不支持录音');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, recorderOptions);
      const actualMimeType = recorder.mimeType || mimeType || 'audio/webm';
      recorderRef.current = recorder;

      recorder.ondataavailable = (evt) => {
        if (evt.data.size > 0) chunksRef.current.push(evt.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        const dataUrl = await blobToDataUrl(blob);
        setAudioUrl(dataUrl);
        setRecordingState('evaluating');

        // 调用 LLM 评估
        const evalResult = await evaluateVocabSpeaking(blob, normalized.en, normalized.cn);
        setResult(evalResult);
        setRecordingState('idle');
      };

      recorder.start();
      setRecordingState('recording');
    } catch (err) {
      console.error('[SpeakingVocabCard] recording failed:', err);
      alert(`录音失败：${(err as Error).message}`);
      setRecordingState('idle');
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
  };

  const hint = normalized.scene || normalized.examples[0];
  const levelMeta = result?.level ? levelLabels[result.level] : null;

  return (
    <div className="session-card vocab-card">
      <div className="vocab-head">
        <div>
          <p className="eyebrow">口语词卡 · C → E 开口说 · LLM 判定</p>
          <h3>{normalized.cn || '词汇释义未提供'}</h3>
          {hint ? <p className="session-meta">提示：{hint}</p> : null}
        </div>
        <div className="pill-row">
          <span className="pill subtle">speaking-phrase</span>
          {normalized.important ? <span className="pill warn">重要表达</span> : null}
        </div>
      </div>

      <div className="vocab-input-block">
        <label className="section-label">请开口说出对应的英文短语/句子</label>

        {/* 录音按钮 */}
        <div className="answer-row" style={{ marginTop: '1rem' }}>
          {recordingState === 'idle' && (
            <>
              <button
                type="button"
                className="ans-btn primary"
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                style={{ flex: 1 }}
              >
                按住录音 🎤
              </button>
            </>
          )}
          {recordingState === 'recording' && (
            <button type="button" className="ans-btn warn" style={{ flex: 1 }} disabled>
              录音中... 🔴 松开停止
            </button>
          )}
          {recordingState === 'evaluating' && (
            <button type="button" className="ans-btn ghost" style={{ flex: 1 }} disabled>
              LLM 评估中...
            </button>
          )}
        </div>

        {/* 评估结果 */}
        {result && (
          <div style={{ marginTop: '1.5rem' }}>
            <div className={`answer-status ${result.passed ? 'success' : 'danger'}`}>
              {result.passed ? '✅ 通过' : '❌ 未通过'}
              {levelMeta && ` · ${levelMeta.label}`}
            </div>

            {result.transcript && (
              <div style={{ marginTop: '0.5rem' }}>
                <p className="session-meta">你说的：{result.transcript}</p>
              </div>
            )}

            <div style={{ marginTop: '0.5rem' }}>
              <p className="session-meta">目标答案：{normalized.en}</p>
              {normalized.acceptableAnswers.length > 1 && (
                <p className="session-meta" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  可接受：{normalized.acceptableAnswers.slice(0, 3).join(' / ')}
                </p>
              )}
            </div>

            {normalized.examples.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-muted)', borderRadius: '4px' }}>
                <p className="session-meta" style={{ marginBottom: '0.25rem' }}>
                  例句：
                </p>
                <p style={{ fontSize: '0.9rem' }}>{normalized.examples[0]}</p>
              </div>
            )}

            {/* 重新录音 */}
            <button
              type="button"
              className="ans-btn ghost"
              onClick={() => {
                setResult(null);
                setAudioUrl(null);
              }}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              重新录音
            </button>
          </div>
        )}

        {result?.error && (
          <p className="answer-status danger" style={{ marginTop: '1rem' }}>
            评估失败：{result.error}
          </p>
        )}

        {/* 音频播放（可选） */}
        {audioUrl && !result && (
          <div style={{ marginTop: '1rem' }}>
            <audio src={audioUrl} controls style={{ width: '100%' }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeakingVocabCard;
